/**
 * Relative timestamps for lists (chats, drafts, agents).
 *
 * Industry pattern (Slack / Linear / GitHub-ish):
 * - Just now → minutes → hours
 * - Yesterday (calendar day)
 * - N days ago (up to 6 days)
 * - Then a short date: "Aug 3" (same year) / "Aug 3, 2025" (older)
 */
export function formatRelativeTime(value?: string | null, options?: { empty?: string }): string {
  const empty = options?.empty ?? "";
  if (!value) return empty;

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return empty;

  const now = Date.now();
  const diffMs = now - date.getTime();
  if (diffMs < 0) return formatShortDate(date);

  const minutes = Math.floor(diffMs / 60_000);
  if (minutes < 1) return "Just now";
  if (minutes < 60) return `${minutes} min ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;

  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const startOfYesterday = new Date(startOfToday);
  startOfYesterday.setDate(startOfYesterday.getDate() - 1);
  if (date >= startOfYesterday && date < startOfToday) return "Yesterday";

  const days = Math.floor(diffMs / 86_400_000);
  if (days < 7) return `${days} day${days === 1 ? "" : "s"} ago`;

  return formatShortDate(date);
}

function formatShortDate(date: Date): string {
  const sameYear = date.getFullYear() === new Date().getFullYear();
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    ...(sameYear ? {} : { year: "numeric" }),
  });
}

/** Chat transcript header — relative when recent, else "Mon, Aug 10 at 7:16 PM". */
export function formatChatTranscriptDate(
  value?: string | null,
  options?: { empty?: string },
): string {
  const empty = options?.empty ?? "";
  if (!value) return empty;

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return empty;

  const now = Date.now();
  const diffMs = now - date.getTime();

  if (diffMs >= 0) {
    const minutes = Math.floor(diffMs / 60_000);
    if (minutes < 1) return "Just now";
    if (minutes < 60) return `${minutes} min ago`;

    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;

    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);
    const startOfYesterday = new Date(startOfToday);
    startOfYesterday.setDate(startOfYesterday.getDate() - 1);
    if (date >= startOfYesterday && date < startOfToday) return "Yesterday";

    const days = Math.floor(diffMs / 86_400_000);
    if (days < 7) return `${days} day${days === 1 ? "" : "s"} ago`;
  }

  const weekday = date.toLocaleDateString(undefined, { weekday: "short" });
  const month = date.toLocaleDateString(undefined, { month: "short" });
  const day = date.getDate();
  const time = date.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
  const sameYear = date.getFullYear() === new Date().getFullYear();
  const yearSuffix = sameYear ? "" : `, ${date.getFullYear()}`;

  return `${weekday}, ${month} ${day}${yearSuffix} at ${time}`;
}
